import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Bell } from 'lucide-react';
import { NotificationsDrawer } from 'components/Resi/Header/NotificationsDrawer';
import {
    clearNotificationsBadge,
    markNotificationsReadRequest
} from 'store/actions/Notifications';
import { usePermissions } from 'hooks/usePermissions';
import { OrgPermissions, moduleKeys } from 'utils/permissions';

// Manager-side counterpart to the resident bell in `components/Resi/Header/TopNavigation.jsx` — same
// visual language, same live `state.notifications` slice (forked for operators in `store/sagas/User.js`),
// same drawer component with its resident-only "Access log" tab turned off since managers don't have
// that sub-category (their notifications are all credential-grant arrivals today).
//
// Gated on module AND permission, matching every other credential-grants surface: the route
// (`routes/MainRoutes.js` -> `RouteGuard`) and the nav item
// (`layout/MainLayout/Drawer/DrawerContent/Navigation/index.js`). The API gates the same way —
// `CredentialGrantsArrivedNotifier` sends nothing to an org without the module — so a
// permission-only check here would render a bell whose only destination 404s.
//
// Same reasoning extends the gate to pending-approval invites: `InviteTransitionNotifier`
// notifies every `org_dashboard` + `manage_occupants` holder (see
// `invite-transition.notifier.ts`), which is neither credential-grants nor reports access —
// without this, that audience would get the Firestore doc but no bell to see it in.
export default function NotificationBell() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const dispatch = useDispatch();
    const { items = [], badge = '0' } = useSelector(state => state.notifications || {});
    const activeOrg = useSelector(state => state.user.activeOrg);
    const userData = useSelector(state => state.user.userData);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleToggle = () => {
        const nextOpen = !drawerOpen;

        setDrawerOpen(nextOpen);

        if (nextOpen) {
            dispatch(clearNotificationsBadge());
            dispatch(markNotificationsReadRequest());
        }
    };

    const hasModule = activeOrg?.modules?.includes(
        moduleKeys.CREDENTIAL_SHARING_CONSUMER
    );

    const hasCredentialGrantsAccess =
        hasModule && hasPermission(OrgPermissions.MANAGE_CREDENTIAL_GRANTS);

    // Consumer-issued devices (SD-3287): `ConsumerIssuedDeviceNotifier` addresses the org's
    // MANAGE_ORG_DEVICES holders (falling back to MANAGE_OPERATORS / primary managers), none of
    // which is covered by the gates above.
    const hasConsumerDevicesAccess =
        hasModule &&
        (hasPermission(OrgPermissions.MANAGE_ORG_DEVICES) ||
            hasPermission(OrgPermissions.MANAGE_OPERATORS));

    // `activeOrg` is the raw org doc (no `type` field) — the supervisor type lives on
    // `userData.orgs[orgId].type`, org-scoped.
    const hasReportsAccess =
        hasPermission(OrgPermissions.MANAGE_REPORTS) ||
        userData?.orgs?.[activeOrg?.org_id]?.type === 'supervisor';

    const hasPendingApprovalAccess =
        activeOrg?.modules?.includes(moduleKeys.ORG_DASHBOARD) &&
        hasPermission(OrgPermissions.ORG_DASHBOARD) &&
        hasPermission(OrgPermissions.MANAGE_OCCUPANTS);

    if (
        !hasCredentialGrantsAccess &&
        !hasConsumerDevicesAccess &&
        !hasReportsAccess &&
        !hasPendingApprovalAccess
    )
        return null;

    // MainLayout's Toolbar takes the theme default rather than pinning its own height the way the
    // resi AppBar does, so the drawer has to be told where the header actually ends.
    const headerOffset =
        typeof theme.mixins?.toolbar?.minHeight === 'number'
            ? theme.mixins.toolbar.minHeight
            : 64;

    return (
        <>
            {/* Hidden while the drawer is open, so it does not sit on top of it. */}
            <Tooltip title={drawerOpen ? '' : 'Notifications'}>
                <IconButton
                    aria-label="Notifications"
                    onClick={handleToggle}
                    sx={{
                        bgcolor: '#f8f8f8',
                        border: '0.762px solid #dedede',
                        borderRadius: '999px',
                        width: '32px',
                        height: '32px',
                        minWidth: '32px',
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&:hover': {
                            bgcolor: '#efefef'
                        }
                    }}
                >
                    <Bell size={17.455} color="#212322" />
                    {badge !== '0' && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-5px',
                                left: '-2px',
                                width: '14px',
                                height: '14px',
                                bgcolor: '#33C78F',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '8.5px',
                                    lineHeight: '6.857px',
                                    color: 'white'
                                }}
                            >
                                {badge}
                            </Typography>
                        </Box>
                    )}
                </IconButton>
            </Tooltip>

            {/*
              `showOrgName` is manager-only. The notifications query is uid-scoped, not org-scoped,
              so a manager holding the permission in more than one org gets every org's
              notifications in one list — and the body text only ever names the SPONSORING org, so
              without `org_name` nothing says which of their own orgs a grant landed in. Residents
              never pass it, so their rows are unchanged.
            */}
            <NotificationsDrawer
                items={items}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                showTabs={false}
                showOrgName
                headerOffset={headerOffset}
            />
        </>
    );
}
